/**
 * Pro entry requires a completed auth session AND a password that has
 * been set / reset (or an admin reauth that forces a new password).
 *
 * Signals:
 *   - user_metadata.must_set_password === true  (admin send-signin / reauth)
 *   - user_metadata.registration_complete === false && user_type professional
 *   - sessionStorage gate while Set/Reset password pages hold a live
 *     session before updateUser({ password }) succeeds
 */

export const PRO_PASSWORD_GATE_KEY = 'pro_password_gate_active';
export const PASSWORD_SETUP_IN_PROGRESS_KEY = 'password_setup_in_progress';

type AuthUserLike = {
  user_metadata?: Record<string, unknown> | null;
} | null | undefined;

/** True when this Auth user must set/reset a password before Pro app entry. */
export function proNeedsPasswordSetup(user: AuthUserLike): boolean {
  if (!user) return false;
  const meta = user.user_metadata ?? {};
  if (meta.must_set_password === true) return true;
  if (meta.user_type !== 'professional') return false;
  return meta.registration_complete === false;
}

export function isProPasswordGateActive(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return (
    sessionStorage.getItem(PRO_PASSWORD_GATE_KEY) === 'true' ||
    sessionStorage.getItem(PASSWORD_SETUP_IN_PROGRESS_KEY) === 'true'
  );
}

/** Call when a recovery / set-password session is established (before submit). */
export function markProPasswordGateActive(): void {
  try {
    sessionStorage.setItem(PRO_PASSWORD_GATE_KEY, 'true');
  } catch {
    // ignore quota / private mode
  }
}

export function clearProPasswordGate(): void {
  try {
    sessionStorage.removeItem(PRO_PASSWORD_GATE_KEY);
    sessionStorage.removeItem(PASSWORD_SETUP_IN_PROGRESS_KEY);
  } catch {
    // ignore
  }
}

/** Metadata patch after a successful password set/reset. */
export function proPasswordClearedMetadata(): Record<string, unknown> {
  return {
    registration_complete: true,
    must_set_password: false,
  };
}
